import { loadStripe } from '@stripe/stripe-js'
import analytics from '~/services/analytics'
import API_URLS, { setAPIUserInfo } from '~/core/api-urls'
import { PLAN_TYPES, EXPIRED_SUBSCRIPTION_STATUSES } from '~/core/constants'
import envData from '~/core/env'
import appConfig from '../app-config'

let accessToken
let stripe
let userData

const ERROR_CODES = {
  TOKEN_EXPIRED: 'TOKEN_EXPIRED'
}

const {
  getStripeProductUrl,
  stripeCancelUrl,
  stripeSuccessUrl
} = appConfig.current

const RETRIES_COUNT = 1
const RETRY_DELAY = 2000

const defaultPaymentMethod = {
  zip: '',
  country: {
    label: 'US',
    value: 'US'
  }
}

const makeRequest = ({ method = 'GET', url, body, format = 'json', attemptCount = 0 } = {}) =>
  new Promise((resolve, reject) => {
    fetch(url, {
      method,
      body: JSON.stringify(body),
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    })
      .then(resp => {
        if (resp.ok) {
          if (format) {
            return resp[format]()
          }

          return resp
        }

        if (resp.status === 401) {
          throw new Error(ERROR_CODES.TOKEN_EXPIRED)
        }

        throw new Error(resp)
      })
      .then(resolve)
      .catch(err => {
        if (attemptCount < RETRIES_COUNT) {
          setTimeout(() => {
            makeRequest({ method, url, body, format, attemptCount: attemptCount + 1 })
              .then(resolve)
              .catch(reject)
          }, RETRY_DELAY)
        } else {
          reject(err)
        }
      })
  })

const fetchClientSecret = () => makeRequest({ method: 'POST', url: API_URLS.CARD_CONTROLLER })

export const setAPIInfo = async ({ accessToken: token, userInfo }) => {
  accessToken = token
  setAPIUserInfo(userInfo)
  stripe = await loadStripe(envData.stripeKey)
  userData = userInfo
}

export const getStripe = () => stripe

export const fetchDefaultPaymentMethod = async () => {
  try {
    const resp = await makeRequest({ url: API_URLS.DEFAULT_PAYMENT_METHOD })
    if (resp.address && resp.address.country) {
      const contryCode = resp.address.country

      return {
        zip: resp.address.postalCode || '',
        country: {
          label: contryCode,
          value: contryCode
        }
      }
    }

    return defaultPaymentMethod
  } catch {
    return defaultPaymentMethod
  }
}

export const updatePaymentInfo = async ({
  cardElement,
  zip,
  country
}) => {
  analytics.trackInitEvent(analytics.EVENT_NAMES.UPDATE_CARD_INFO)

  try {
    const secret = await fetchClientSecret()
    const result = await stripe.confirmCardSetup(secret.clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          email: userData.email,
          address: {
            postal_code: zip,
            country
          }
        }
      }
    })

    if (result.error) {
      analytics.trackFailedEvent(analytics.EVENT_NAMES.UPDATE_CARD_INFO, {
        reason: result.error.message
      })
      throw new Error(result.error.message)
    }
  } catch (err) {
    const errorMessage = err.message === ERROR_CODES.TOKEN_EXPIRED
      ? 'Please login again to change the payment details'
      : 'Failed updating payment info'

    analytics.trackFailedEvent(analytics.EVENT_NAMES.UPDATE_CARD_INFO, {
      reason: errorMessage
    })
    throw new Error(errorMessage)
  }
}

export const getProducts = () => makeRequest({ url: getStripeProductUrl() })

export const getProSubscription = async () => {
  const subscriptions = await getCurrentPlan()
  return subscriptions.find(sub => sub.plan.planType === PLAN_TYPES.PRO)
}

export const getTeamMembersCount = async () => {
  const url = API_URLS.TEAM.MEMBERS

  try {
    const members = await makeRequest({ url })
    return members.filter(m => !m.pendingInvitation).length
  } catch (err) {
    throw new Error(err)
  }
}

export const createSession = async (planId, coupon) => {
  const queryParams = new URLSearchParams(window.location.search)
  const defaultCancelUrl = stripeCancelUrl
  const defaultSuccessUrl = stripeSuccessUrl

  const {
    cancelRedirectUrl = defaultCancelUrl,
    successRedirectUrl = defaultSuccessUrl
  } = Object.fromEntries(queryParams.entries())

  const params = {
    cancelRedirectUrl,
    successRedirectUrl
  }

  if (coupon) {
    params.couponKey = coupon
  }

  const url = API_URLS.CREATE_SUBSCRIPTION(params)

  try {
    const response = await makeRequest({ method: 'POST', url, body: { planId } })
    return response
  } catch (err) {
    throw new Error(err)
  }
}

export const createNoCreditCardSession = async () => {
  try {
    await makeRequest({ method: 'POST', url: API_URLS.CREATE_FREE_SUBSCRIPTION, format: null, body: {} })
    await pollForSubscriptionStatus()
  } catch (err) {
    throw new Error(err)
  }
}

export const getCurrentPlan = () => {
  const url = API_URLS.SUBSCRIPTIONS

  return makeRequest({ method: 'GET', url })
}

export const validateCoupon = async key => {
  const url = API_URLS.COUPON(key)

  try {
    const response = await makeRequest({ url })
    return response
  } catch (err) {
    throw new Error(err)
  }
}

export const getDomainDetectionStatus = async () => {
  const url = API_URLS.customerConferenceSettings

  try {
    const response = await makeRequest({ url })
    return Boolean(response.domain)
  } catch (err) {
    throw new Error(err)
  }
}

export const pollForSubscriptionStatus = () => new Promise((resolve, reject) => {
  let tries = 0

  const processResponse = subscriptions => {
    if (subscriptions) {
      if (subscriptions.some(sub => sub.plan.planType === PLAN_TYPES.PRO && !EXPIRED_SUBSCRIPTION_STATUSES.includes(sub.status))) {
        resolve()
        return
      }
      doPoll()
    } else {
      doPoll()
    }
  }

  const doPoll = () => {
    setTimeout(() => {
      if (++tries === 20) {
        reject(new Error('Retries limit reached'))
        return
      }

      getCurrentPlan()
        .then(processResponse)
        .catch(doPoll)
    }, 2000)
  }

  doPoll()
})

export const fetchInvoices = () => makeRequest({ url: API_URLS.INVOICES })

export const cancelAccount = async () => {
  await makeRequest({ method: 'DELETE', url: API_URLS.DELETE_SUBSCRIPTION })
  window.location = appConfig.current.getLogoutUrl()
}
